import { Suspense, lazy, useState } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { apiClient } from './services/api'
import Header from './components/Header'
import ReviewForm from './components/ReviewForm'
import ResultsPanel from './components/ResultsPanel'
import SmbReviewTest from './components/SmbReviewTest'
import RegistrationWizard from './components/registration/RegistrationWizard'
import LoginPage from './components/LoginPage'
import ForgotPasswordPage from './components/ForgotPasswordPage'
import ResetPasswordPage from './components/ResetPasswordPage'
import Dashboard from './components/dashboard/Dashboard'
import OnboardingPage from './components/OnboardingPage'
import OAuthRelayPage from './components/OAuthRelayPage'
import CheckYourEmailPage from './components/CheckYourEmailPage'
import VerifyEmailPage from './components/VerifyEmailPage'
import { ProcessingResult } from './services/api'
import { FlaskConical, Building2 } from 'lucide-react'
import AdminErrorBoundary from './components/admin/AdminErrorBoundary'

// Lazy-loaded so admin code ships as its own chunk, never bundled into the
// customer-facing app — see docs/admin-panel-design.md ("Isolation architecture").
const AdminLogin = lazy(() => import('./components/admin/AdminLogin'))
const AdminNeedsAttention = lazy(() => import('./components/admin/AdminNeedsAttention'))
const AdminCustomerList = lazy(() => import('./components/admin/AdminCustomerList'))
const AdminCustomerDetail = lazy(() => import('./components/admin/AdminCustomerDetail'))

type Tab = 'llm' | 'smb'

function HomeContent() {
  const [activeTab, setActiveTab] = useState<Tab>('llm')

  // LLM test tab state
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [results, setResults] = useState<ProcessingResult | null>(null)
  const [error, setError] = useState<string>('')

  const handleProcessReview = async (
    reviewText: string,
    rating: number,
    reviewerName: string,
    reviewerEmail: string,
    escalationEmail: string,
  ) => {
    setError('')
    setIsLoading(true)
    try {
      const result = await apiClient.processReview({
        text: reviewText,
        rating,
        reviewer: reviewerName,
        reviewer_email: reviewerEmail || undefined,
        escalation_email: escalationEmail || undefined,
      })
      setResults(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Processing failed')
      setResults(null)
    } finally {
      setIsLoading(false)
    }
  }

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'llm', label: 'LLM Response Test', icon: <FlaskConical className="w-4 h-4" /> },
    { id: 'smb', label: 'SMB Review Test',   icon: <Building2 className="w-4 h-4" /> },
  ]

  return (
    <main className="max-w-7xl mx-auto px-4 py-8">

      {/* Tab bar */}
      <div className="flex gap-1 mb-8 border-b border-gray-200">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-medium rounded-t-lg transition-colors
              ${activeTab === tab.id
                ? 'bg-white border border-b-white border-gray-200 text-primary-600 -mb-px'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* LLM Response Test */}
      {activeTab === 'llm' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <ReviewForm
            onSubmit={handleProcessReview}
            isLoading={isLoading}
            error={error}
          />
          <ResultsPanel results={results} isLoading={isLoading} />
        </div>
      )}

      {/* SMB Review Test */}
      {activeTab === 'smb' && <SmbReviewTest />}

    </main>
  )
}

const HEADERLESS_ROUTES = ['/register', '/login', '/forgot-password', '/reset-password', '/onboarding', '/dashboard', '/oauth-relay', '/check-your-email', '/verify-email']

function App() {
  const { pathname } = useLocation()
  const showHeader = !HEADERLESS_ROUTES.includes(pathname) && !pathname.startsWith('/admin')

  return (
    <div className="min-h-screen bg-rd-bg">
      {showHeader && <Header />}
      <Routes>
        <Route path="/" element={<HomeContent />} />
        <Route path="/register" element={<RegistrationWizard />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/oauth-relay" element={<OAuthRelayPage />} />
        <Route path="/check-your-email" element={<CheckYourEmailPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route
          path="/admin/*"
          element={
            <AdminErrorBoundary>
              <Suspense fallback={null}>
                <Routes>
                  <Route path="login" element={<AdminLogin />} />
                  <Route index element={<AdminNeedsAttention />} />
                  <Route path="customers" element={<AdminCustomerList />} />
                  <Route path="customers/:id" element={<AdminCustomerDetail />} />
                </Routes>
              </Suspense>
            </AdminErrorBoundary>
          }
        />
      </Routes>
    </div>
  )
}

export default App
